import { useNavigation } from '@react-navigation/native';
import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { Fragment } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { formatWithMask } from 'react-native-mask-input';

import {
  profilePrivateSchema,
  ProfilePrivateSchemaDataType,
} from '@validation';
import { IS_MOBILE, IS_WEB, parseApiError } from '@utils';
import {
  Button,
  ButtonsLayout,
  ButtonStyleEnum,
  Field,
  Form,
  FormButton,
  Loader,
  Separator,
} from '@components';
import { useViewerModel } from '@models/Viewer/ViewerModel';
import { ToastService } from '@services';
import { ProfileNavigatorNavigationPropType } from '@navigation';
import { INPUT_PHONE_NUMBER_MASK } from '@constants';

const LoaderContainer = styled(View)({
  flex: 1,
  justifyContent: 'center',
});

const FormContainer = styled(View)({
  maxWidth: ['100%', 600],
  flexGrow: 1,
});

const InnerMobileContainer = styled(View)({
  flex: 1,
  justifyContent: 'space-between',
});

const FormInnerContainer = IS_MOBILE
  ? InnerMobileContainer
  : Fragment;

const cancelButtonStyle = IS_WEB
  ? ButtonStyleEnum.Outline
  : ButtonStyleEnum.Ghost;

function phoneNumberTransformer(value?: string) {
  return formatWithMask({
    text: value,
    mask: INPUT_PHONE_NUMBER_MASK,
  }).masked;
}

export interface IProfileFormPrivateProps {
  hideForm?: () => void;
}

type NavigationPropType =
  ProfileNavigatorNavigationPropType<'UpdateProfilePrivate'>;

export const ProfileFormPrivate = observer<IProfileFormPrivateProps>(
  (props: IProfileFormPrivateProps) => {
    const { t } = useTranslation();
    const nav = useNavigation<NavigationPropType>();

    const viewer = useViewerModel();
    const user = viewer.currentUser;

    async function submitForm(values: ProfilePrivateSchemaDataType) {
      try {
        await viewer.update.run({
          phone: values.phone,
          email: values.email,
        });

        closeForm();
      } catch (error) {
        ToastService.showError(parseApiError(error).message);
      }
    }

    function closeForm() {
      if (IS_WEB && props.hideForm) {
        props.hideForm();
        return;
      }
      nav.goBack();
    }

    if (!user) {
      return (
        <LoaderContainer>
          <Loader size="large" />
        </LoaderContainer>
      );
    }

    const initialValues: ProfilePrivateSchemaDataType = {
      phone: user.phone,
      email: user.email,
    };

    return (
      <Form<ProfilePrivateSchemaDataType>
        onSubmit={submitForm}
        initialValues={initialValues}
        validationSchema={profilePrivateSchema}
        validateOnMount
      >
        <FormInnerContainer>
          <FormContainer>
            <Separator />
            <Field
              name="phone"
              valueTransformer={phoneNumberTransformer}
              placeholder={t('profile.phone')}
              keyboardType="phone-pad"
            />

            <Separator />
            <Field
              name="email"
              autoCapitalize="none"
              placeholder={t('profile.email')}
              keyboardType="email-address"
            />
            <Separator />
          </FormContainer>

          <ButtonsLayout>
            <FormButton>{t('profile.save')}</FormButton>
            <Button
              buttonStyle={cancelButtonStyle}
              onPress={closeForm}
            >
              {t('profile.cancel')}
            </Button>
          </ButtonsLayout>
        </FormInnerContainer>
      </Form>
    );
  },
);
